import React, {
    FC,
    useMemo,
    useState,
} from "react";
import {
    Box,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {IGarageList} from "../../../interfaces/Interfaces";
import {Locale} from "../../../locales/Locale";
import {IGarageListColumns} from "./GarageListColumns";
import {IdlocaGeneric} from "../../../locales/IdlocaGeneric";
import {ILocation} from "../MapView";
import {GarageTableFilters} from "./GarageTableFilters";
import {GarageTableRows} from "./GarageTableRows";
import {GarageCardListMobile} from "./GarageCardListMobile";
import {
    buildCityOptions,
    buildTypeOptions,
} from "../../../utils/garageUtils";

interface IGarageTableProps {
    data: IGarageList[];
    columns: IGarageListColumns[];
    onDetailsClick?: (id: string) => void;
    onRowClick?: (location: ILocation) => void;
}

export const GarageTable: FC<IGarageTableProps> = ({
    data,
    columns,
    onDetailsClick,
    onRowClick,
}) => {
    const [typeFilter, setTypeFilter] = useState<string | number | null>("");
    const [cityFilter, setCityFilter] = useState<string | number | null>("");

    const [filteredData, setFilteredData] = useState<IGarageList[]>(data);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const availableTypeOptions = useMemo(() => buildTypeOptions(data), [data]);
    const availableCityOptions = useMemo(() => buildCityOptions(data), [data]);

    const handleFilter = () => {
        const filtered = data.filter((item) => {
            const matchesType = typeFilter
                ? item.type.toLowerCase().includes(String(typeFilter).toLowerCase())
                : true;

            const matchesCity = cityFilter
                ? item.city?.toLowerCase().includes(String(cityFilter).toLowerCase())
                : true;

            return matchesType && matchesCity;
        });

        setFilteredData(filtered);
    };

    const handleReset = () => {
        setTypeFilter("");
        setCityFilter("");
        setFilteredData(data);
    };

    return (
        <>
            <Box
                display="flex"
                flexDirection={{xs: "column", sm: "row"}}
                gap={2}
                mb={1}
                mt={1}
                alignItems={{xs: "stretch", sm: "center"}}
            >
                <GarageTableFilters
                    typeFilter={typeFilter}
                    cityFilter={cityFilter}
                    setTypeFilter={setTypeFilter}
                    setCityFilter={setCityFilter}
                    onFilter={handleFilter}
                    onReset={handleReset}
                    availableTypeOptions={availableTypeOptions}
                    availableCityOptions={availableCityOptions}
                />
            </Box>
            {isMobile ? (
                <GarageCardListMobile
                    data={filteredData}
                    columns={columns}
                    onDetailsClick={onDetailsClick}
                />
            ) : (
                <TableContainer component={Paper} sx={{overflowX: "auto"}}>
                    <Table aria-label="garage table">
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell key={col.param} sx={{p: 1, height: "16px"}}>
                                        {col.locaId.trim() !== "" && (
                                            <Locale localeId={col.locaId as IdlocaGeneric}/>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <GarageTableRows
                            data={filteredData}
                            columns={columns}
                            onDetailsClick={onDetailsClick}
                            onRowClick={onRowClick}
                        />
                    </Table>
                </TableContainer>
            )}
        </>
    );
};
